import { memo, useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useLocation } from 'react-router-dom'
import SmoothScroll from '@/components/SmoothScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import Profile from '@/components/Profile'
import Banner from '@/components/Banner'
import Story from '@/components/Story'
import SectorsBallon from '@/components/SectorsBallon'
import ParallaxImg from '@/components/ParallaxImg'
import InViewTrigger from '@/components/InViewTrigger'
import RevealText from '@/components/RevealText'
import RevealFadeIn from '@/components/RevealFadeIn'
import PageElements from '@/components/PageElements'
import usePagePrecache from '@/hooks/usePagePrecache'
import Footer from '@/components/Footer'
import * as contentActions from '@/actions/content'
import * as localeActions from '@/actions/locale'
import { getSlug } from '@/utils/path'
import { removeParagraph, removeBr } from '@/utils/strings'
import style from './style'

const useStyles = createUseStyles(style)

const About = () => {
  const classes = useStyles()
  const [isDataFetched, setDataFetched] = useState(false)
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))
  const $root = useRef()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { page, isPageAnimationReady } = useSelector((state) => ({
    page: state.content.pages[stub.current] || {},
    isPageAnimationReady: state.loading.isPageAnimationReady,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const fetchPage = useCallback((slug) => dispatch(contentActions.fetchPage(slug)), [dispatch])
  const setTranslations = useCallback((content) => dispatch(localeActions.setTranslations(content)), [dispatch])

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      setDataFetched(true)
      setTranslations(page)
    }
  }, [page])

  /*------------------------------
  Preload
  ------------------------------*/
  const [load] = usePagePrecache({
    init: isDataFetched,
    sources: [
      { type: 'image', src: page.featured_image },
      // Precache video if exist
      { type: 'video', src: page.acf?.intro_video.url },
    ],
    callback: () => {},
  })

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = useCallback(() => {
    if (Object.keys(page).length === 0) fetchPage(stub.current)
  }, [page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = useCallback(() => {
    return load && (
      <Meta
        title={page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }, [load, page])

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderHero = useCallback(() => {
    return (
      <Hero
        image={page.featured_image}
        video={page.acf.intro_video?.url}
        title={page.title.rendered}
        secondaryImage={page.acf.intro_image.url}
        secondaryTitle={page.acf.title}
        secondaryText={page.acf.text}
        visible={isPageAnimationReady}
      />
    )
  }, [page, isPageAnimationReady])

  /*------------------------------
  Render Profile
  ------------------------------*/
  const renderProfile = useCallback(() => {
    return page.acf.profile.list.length > 0 && (
      <Profile
        title={page.acf.profile.title}
        text={page.acf.profile.text}
        items={page.acf.profile.list}
      />
    )
  }, [page])

  /*------------------------------
  Render Mission & Vision
  ------------------------------*/
  const renderMission = useCallback(() => {
    return (
      Object.keys(page.acf.mission).length > 0
      && page.acf.mission.image
    ) && (
    <Banner
      image={page.acf.mission.image}
      title={page.acf.mission.title}
      text={page.acf.mission.text}
      align="left"
      borderImage={false}
    />
    )
  }, [page])

  /*------------------------------
  Render Mission & Vision
  ------------------------------*/
  const renderVision = useCallback(() => {
    return (
      Object.keys(page.acf.vision).length > 0
      && page.acf.vision.image) && (
      <Banner
        image={page.acf.vision.image}
        title={page.acf.vision.title}
        text={page.acf.vision.text}
        align="right"
        borderImage={false}
      />
    )
  }, [page])

  /*------------------------------
  Render Values
  ------------------------------*/
  const renderValues = useCallback(() => {
    return (
      Object.keys(page.acf.values).length > 0
      && page.acf.values.image) && (
      <Banner
        image={page.acf.values.image}
        title={page.acf.values.title}
        text={removeParagraph(page.acf.values.text)}
        align="left"
        borderImage={false}
      />
    )
  }, [page])

  /*------------------------------
  Render Story
  ------------------------------*/
  const renderStory = useCallback(() => {
    return page.acf.story.year_list.length > 0 && (
      <Story
        title={page.acf.story.title}
        items={page.acf.story.year_list}
      />
    )
  }, [page])

  /*------------------------------
  Render Development
  ------------------------------*/
  const renderDevelopment = useCallback(() => {
    return (
      Object.keys(page.acf.development).length > 0
      && page.acf.development.image) && (
      <>
        <div className={`${classes.wrapper} ${classes.singleImageWrapper}`}>
          <div className={classes.singleImage}>
            <ParallaxImg
              img={page.acf.development.image.url}
              title={removeParagraph(removeBr(page.acf.title))}
            />
          </div>
        </div>
        <div className={`${classes.wrapper} ${classes.columnText}`}>
          <InViewTrigger className={classes.titleContainer}>
            <RevealText
              tag="h2"
              type="lines"
              value={page.acf.development.title}
              lineHeight={1.1}
            />
          </InViewTrigger>
          <InViewTrigger className={classes.textContainer}>
            <RevealFadeIn>
              <div dangerouslySetInnerHTML={{ __html: page.acf.development.text }} />
            </RevealFadeIn>
          </InViewTrigger>
        </div>
      </>
    )
  }, [page])

  /*------------------------------
  Render Sectors
  ------------------------------*/
  const renderSectors = useCallback(() => {
    return page.acf.sectors.sectors_list.length > 0 && (
      <SectorsBallon items={page.acf.sectors.sectors_list} />
    )
  }, [page])

  /*------------------------------
  Render Page Builder
  ------------------------------*/
  const renderPageBuilder = useCallback(() => {
    return page.acf.page_builder && (
      <PageElements elements={page.acf.page_builder} />
    )
  }, [page])

  return (
    <SmoothScroll
      init={load}
      className={`page pageAbout ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {load && (
        <>
          <div className={classes.page}>
            {renderHero()}
            {renderProfile()}
            {renderMission()}
            {renderVision()}
            {renderValues()}
            {renderStory()}
            <div className={classes.backgroundLight}>
              {renderDevelopment()}
              {renderSectors()}
            </div>
            {renderPageBuilder()}
          </div>
          <Footer />
        </>
      )}
    </SmoothScroll>
  )
}

export default memo(About)
